/**
 * Hjälpare för butikens flair-trappa (namnstilar och titelplattor).
 *
 * Namnet renderas via ETT fält, `flair_name_color`, som bär hela trappan:
 * börjar värdet med `#` är det en flat hexfärg (nivå 1) och sätts som
 * CSS-variabel; annars är det en presetnyckel (nivå 2-5) som slås upp till
 * en CSS-klass i `components/shop/FlairStyles.css`.
 *
 * Nycklarnas sanning ligger i backend (`shop/flair_presets.py`) — den listan
 * och CSS-klasserna här måste hållas i takt.
 */

/**
 * Bara gemener, siffror och understreck får bli klassnamn.
 *
 * Värdet kommer från databasen och inte från användarinmatning, men ett
 * klassnamn byggt av fri text är ändå en injektionsyta som inte kostar
 * något att stänga.
 */
const SAFE_KEY = /^[a-z0-9_]+$/;

/**
 * Bygg props för ett spelarnamn utifrån den utrustade namnstilen.
 *
 * @param {string|null|undefined} value - `flair_name_color`: `#rrggbb` eller
 *   en presetnyckel (t.ex. `tiger_tooth`).
 * @param {string} [base='player-name'] - Grundklassen som alltid sätts.
 * @returns {{className: string, style: object|undefined}} Props att sprida
 *   på elementet. `style` används bara för att bära hexfärgen som
 *   CSS-variabel — all faktisk styling bor i CSS.
 */
export const flairNameProps = (value, base = 'player-name') => {
  if (!value) return { className: base, style: undefined };
  if (value.startsWith('#')) {
    return {
      className: `${base} has-flair-color`,
      style: { '--flair-color': value },
    };
  }
  if (!SAFE_KEY.test(value)) return { className: base, style: undefined };
  return { className: `${base} flair-name flair-name--${value}`, style: undefined };
};

/**
 * Bygg props för en titelplatta.
 *
 * Plattan är fristående från titeltexten: spelaren köper plattan en gång och
 * den gäller vilken titel hen än bär. Tomt värde = ingen platta (grå text).
 *
 * Den färgade plattan (`chip`) tonas efter spelarens namnfärg när den är en
 * flat hexfärg, så profilen hänger ihop. Bär spelaren en presetstil finns
 * ingen hexkod att låna — då faller CSS:en tillbaka på accentfärgen.
 *
 * @param {string|null|undefined} style - `flair_title_style`: `chip`, `gold`
 *   eller `holo`.
 * @param {string|null|undefined} [nameColor] - `flair_name_color`, används
 *   bara när den är en hexfärg.
 * @param {string} [base='flair-title'] - Grundklassen som alltid sätts.
 * @returns {{className: string, style: object|undefined}} Props att sprida.
 */
export const flairPlateProps = (style, nameColor, base = 'flair-title') => {
  if (!style || !SAFE_KEY.test(style)) return { className: base, style: undefined };
  return {
    className: `${base} flair-plate flair-plate--${style}`,
    style: nameColor && nameColor.startsWith('#')
      ? { '--flair-color': nameColor }
      : undefined,
  };
};
